import {
  Breakpoints,
  DROPPING_ITEM,
  GRID_BREAKPOINTS,
  GRID_COLS,
  GRID_DRAGGABLE_HANDLE,
  GRID_MARGIN,
  GRID_ROW_HEIGHT,
} from "components/dashboard/grid/config"
import React, { FC, DragEvent, memo } from "react"
import { Layout as ReactGridLayout, Layouts, Responsive, WidthProvider } from "react-grid-layout"
import {
  disableGridLayoutChangeDetection,
  enableGridLayoutChangeDetection,
  getBuiltInWidgetConfigByWidget,
  getGridIds,
  getGridLayouts,
  getHoveredLayout,
  gridLayoutChangeDetectionEanbled,
} from "components/dashboard/grid/helpers"
import { Empty } from "components/dashboard/grid/empty"
import { Widget } from "domainModels/widget"
import { useIsDashboardOwner, useUpdateDashboard } from "reactQuery/dashboard"
import { Group } from "domainModels/group"
import { useDeleteGroup } from "reactQuery/group"
import { useCombineWidgetsToGroup, useUpdateWidget } from "reactQuery/widget"
import { Layout } from "domainModels/layout"
import { useInsertLayout, useUpdateLayouts } from "reactQuery/layout"
import toast from "react-hot-toast"
import { TOAST_DASHBOARD_UPDATE } from "components/dashboard/toastConfig"
import { GroupWidget } from "components/dashboard/widgets/groupWidget"
import { ExternalUrlWidget } from "components/dashboard/widgets/externalUrlWidget"
import { useUser } from "lib/supabase/auth"
import { UseMutateAsyncFunction } from "react-query"
import { Dashboard } from "domainModels/dashboard"
import { MarkdownWidget } from "components/dashboard/widgets/markdownWidget"
import { BuiltInWidget } from "components/dashboard/widgets/builtInWidget"

interface Props {
  dashboard: Dashboard
  isPrivate: boolean
  widgets: Widget[]
  publicWidgets: Widget[]
  groups?: Group[] | null
  updateLayout: UseMutateAsyncFunction<
    void,
    Error,
    {
      layout: Layout
      dashboardId: number
    },
    unknown
  >
}

let hoveredLayout: ReactGridLayout | null = null
const reactGridItemHoverClass = "react-grid-item-hover"
const setHoveredGridItem = (item: ReactGridLayout) => {
  document.querySelector(`[data-id=${item.i}]`)?.classList.add(reactGridItemHoverClass)
  hoveredLayout = item
}

const removeHoveredGridItem = () => {
  if (hoveredLayout) {
    document.querySelector(`[data-id=${hoveredLayout?.i}]`)?.classList.remove(reactGridItemHoverClass)
    hoveredLayout = null
  }
}

export const GridContent: FC<Props> = memo(({ dashboard, isPrivate, widgets, publicWidgets, groups, updateLayout }) => {
  const dashboardId = dashboard.id!
  const { user } = useUser()
  const { mutateAsync: combineWidgetsToGroup } = useCombineWidgetsToGroup()
  const { mutateAsync: deleteGroup } = useDeleteGroup()
  const { mutateAsync: updateWidget } = useUpdateWidget()
  const { mutateAsync: updateLayouts } = useUpdateLayouts()
  const { mutateAsync: updateDashboard } = useUpdateDashboard()
  const { mutateAsync: insertLayout } = useInsertLayout()
  const isDashboardOwner = useIsDashboardOwner()
  const finalLayouts = getGridLayouts(widgets, groups)

  const onLayoutChange = async (_layouts: ReactGridLayout[], allLayouts: Layouts) => {
    if (isDashboardOwner && gridLayoutChangeDetectionEanbled() && !window.DRAGGING_GROUP_ITEM) {
      disableGridLayoutChangeDetection()
      const diffs: Layout[] = []

      Object.keys(allLayouts).forEach((breakpoint) => {
        allLayouts[breakpoint].forEach((reactGridLayout) => {
          const storedLayout = finalLayouts[breakpoint].find((l) => l.i === reactGridLayout.i)

          if (
            storedLayout &&
            (reactGridLayout.w !== storedLayout.w ||
              reactGridLayout.h !== storedLayout.h ||
              reactGridLayout.x !== storedLayout.x ||
              reactGridLayout.y !== storedLayout.y)
          ) {
            const { widgetId, groupId } = getGridIds(reactGridLayout!.i)
            const widget = widgets.find((w) => w.id?.toString() === widgetId)
            const group = groups?.find((g) => g.id?.toString() === groupId)
            const originalLayout = widget?.layout || group?.layout

            if (originalLayout && reactGridLayout) {
              diffs.push({
                ...originalLayout,
                ...{
                  [breakpoint]: {
                    ...originalLayout[breakpoint as Breakpoints],
                    x: reactGridLayout.x,
                    y: reactGridLayout.y,
                    w: reactGridLayout.w,
                    h: reactGridLayout.h,
                  },
                },
              })
            }
          }
        })
      })

      if (diffs.length) {
        if (user) {
          await toast.promise(
            Promise.all(diffs.map((diff) => updateLayout({ layout: diff, dashboardId }))),
            TOAST_DASHBOARD_UPDATE
          )
          updateDashboard(dashboard)
        } else {
          await toast.promise(updateLayouts({ layouts: diffs, dashboardId }), TOAST_DASHBOARD_UPDATE)
        }
      }

      enableGridLayoutChangeDetection()
    }
  }

  const onDrag = (layouts: ReactGridLayout[], _layout: ReactGridLayout, target: ReactGridLayout) => {
    const { groupId } = getGridIds(target.i)
    if (!!groupId) {
      // For groups we don't need to find hovered layouts as we don't allow this for now
      return
    }
    disableGridLayoutChangeDetection()
    const foundHovered = getHoveredLayout(layouts, target)
    const dragingWidget = widgets.find((w) => w.id?.toString() === window.DRAGGING_GROUP_ITEM)
    const isAlreadyInGroup = dragingWidget?.group?.id?.toString() === foundHovered?.i.replace("group-", "")

    if (foundHovered && !foundHovered.static && !isAlreadyInGroup && hoveredLayout?.i !== foundHovered.i) {
      removeHoveredGridItem()
      setHoveredGridItem(foundHovered)
    } else if (isAlreadyInGroup || (!foundHovered && hoveredLayout)) {
      removeHoveredGridItem()
    }
    enableGridLayoutChangeDetection()
  }

  // Called, when a layout is dragged into another one
  const onDragStop = async (_layouts: ReactGridLayout[], layout: ReactGridLayout) => {
    if (hoveredLayout) {
      disableGridLayoutChangeDetection()

      const { widgetId: sourceWidgetId } = getGridIds(layout.i)
      const { groupId: targetGroupId, widgetId: targetWidgetId } = getGridIds(hoveredLayout.i)
      const targetGroup = groups?.find((g) => g.id?.toString() === targetGroupId)
      const sourceWidget = widgets?.find((w) => w.id?.toString() === sourceWidgetId)
      const targetWidget = widgets?.find((w) => w.id?.toString() === targetWidgetId)

      await toast.promise(
        new Promise<void>(async (resolve) => {
          if (targetGroup && sourceWidget) {
            // Add to existing group
            await updateWidget({
              dashboardId,
              widget: {
                ...sourceWidget,
                group: targetGroup,
                layout: null,
                information: undefined,
              },
            })
          } else if (sourceWidget && targetWidget && hoveredLayout) {
            // Combine widgets to group
            await combineWidgetsToGroup({
              sourceWidget,
              targetWidget,
              targetLayout: hoveredLayout,
              dashboardId,
              isPrivate,
            })
          }

          resolve()
        }),
        TOAST_DASHBOARD_UPDATE
      )

      removeHoveredGridItem()
      enableGridLayoutChangeDetection()
      updateDashboard(dashboard)
    }
  }

  // Called, when an group layout is dragged outside the current group
  const onDrop = async (_layouts: ReactGridLayout[], layoutItem: ReactGridLayout, ev: Event) => {
    disableGridLayoutChangeDetection()

    const sourceWidgetId = (ev as unknown as DragEvent).dataTransfer.getData("text/plain")
    const sourceWidget = widgets.find((w) => w.id?.toString() === sourceWidgetId)
    const sourceGroup = sourceWidget?.group

    await toast.promise(
      new Promise<void>(async (resolve) => {
        if (hoveredLayout?.i) {
          const { groupId: targetGroupId, widgetId: targetWidgetId } = getGridIds(hoveredLayout.i)
          const targetGroup = groups?.find((g) => g.id?.toString() === targetGroupId)
          const targetWidget = widgets?.find((w) => w.id?.toString() === targetWidgetId)

          if (sourceWidget && targetGroup) {
            if (sourceGroup) {
              // On last widget - first delete group, then update widget,
              // otherwise RLS will fails, as the link to user id is lost.
              const remainingWidgets = widgets.filter((w) => w.group?.id === sourceGroup.id && w.id !== sourceWidget.id)
              if (remainingWidgets.length === 0) {
                await deleteGroup({ dashboardId, group: sourceGroup })
              }
            }

            // Add to other group
            await updateWidget({
              dashboardId,
              widget: {
                ...sourceWidget,
                group: targetGroup,
                layout: null,
                information: undefined,
              },
            })
          } else if (sourceWidget && targetWidget) {
            // Combine with widget to new group
            await combineWidgetsToGroup({
              sourceWidget,
              targetWidget,
              targetLayout: hoveredLayout,
              dashboardId,
              isPrivate,
            })
          }
        } else if (sourceWidget && sourceGroup) {
          // Add back to dashboard
          const layout = {
            w: layoutItem.w,
            h: layoutItem.h,
            x: layoutItem.x,
            y: layoutItem.y,
            minW: layoutItem.minW,
            minH: layoutItem.minH,
          }
          const newLayout = await insertLayout(
            new Layout({
              user_id: user?.id,
              is_private: isPrivate,
              xs: layout,
              md: layout,
              lg: layout,
              xl: layout,
              xxl: layout,
            })
          )

          // On last widget - first delete group, then update widget,
          // otherwise RLS will fails, as the link to user id is lost.
          const remainingWidgets = widgets.filter((w) => w.group?.id === sourceGroup.id && w.id !== sourceWidget.id)
          if (remainingWidgets.length === 0) {
            await deleteGroup({ dashboardId, group: sourceGroup })
          }

          await updateWidget({
            dashboardId,
            widget: {
              ...sourceWidget,
              layout: newLayout,
              group: null,
              information: undefined,
            },
          })
        }

        resolve()
      }),
      TOAST_DASHBOARD_UPDATE
    )

    removeHoveredGridItem()
    enableGridLayoutChangeDetection()
    updateDashboard(dashboard)
    window.DRAGGING_GROUP_ITEM = undefined
  }

  if (!finalLayouts.xxl.length) {
    return <Empty />
  }

  return (
    <ResponsiveGridLayout
      isBounded
      isResizable={isDashboardOwner}
      isDraggable={isDashboardOwner}
      isDroppable={isDashboardOwner}
      measureBeforeMount
      layouts={finalLayouts}
      breakpoints={GRID_BREAKPOINTS}
      droppingItem={DROPPING_ITEM}
      cols={GRID_COLS}
      draggableHandle={GRID_DRAGGABLE_HANDLE}
      rowHeight={GRID_ROW_HEIGHT}
      margin={GRID_MARGIN}
      onLayoutChange={onLayoutChange}
      onDrag={onDrag}
      onDragStop={onDragStop}
      onDrop={onDrop}
      onDropDragOver={(_ev) => {
        const widget = widgets.find((w) => w.id?.toString() === window.DRAGGING_GROUP_ITEM)
        const publicWidget = publicWidgets.find((w) => w.information.id === widget?.information.id)

        // We can't access dataTransfer from the onDrop event (browser limitation...) so we work with the global variable DRAGGING_GROUP_ITEM
        if (publicWidget?.layout) {
          return publicWidget.layout.xxl
        }

        return { w: DROPPING_ITEM.w, h: DROPPING_ITEM.h, minW: DROPPING_ITEM.minW, minH: DROPPING_ITEM.minH }
      }}
    >
      {finalLayouts?.xl.map((layout) => {
        const widget = widgets.find((w) => w.id?.toString() === layout.i.replace("widget-", ""))
        const group = groups?.find((g) => g.id?.toString() === layout.i.replace("group-", ""))
        const groupWidgets = widgets.filter((w) => w.group?.id === group?.id)
        const builtInWidget = widget ? getBuiltInWidgetConfigByWidget(widget) : null

        return (
          <div data-id={layout.i} key={layout.i}>
            {group && <GroupWidget group={group} groupWidgets={groupWidgets} />}
            {widget && builtInWidget && <BuiltInWidget widgetConfig={builtInWidget} widget={widget} />}
            {widget?.information.external_url && <ExternalUrlWidget widget={widget} />}
            {widget?.information.markdown && <MarkdownWidget widget={widget} />}
          </div>
        )
      })}
    </ResponsiveGridLayout>
  )
})

const ResponsiveGridLayout = WidthProvider(Responsive)
